import { outcomeForElement } from "./proposal";
import { OUTCOMES, type FixtureElement, type MappingRequest, type Outcome } from "./types";

interface IndexedElement {
  readonly element: FixtureElement;
  readonly searchText: string;
}

export interface FixtureDatasetIndex {
  readonly records: number;
  readonly entries: readonly IndexedElement[];
  readonly elementsById: ReadonlyMap<string, FixtureElement>;
}

export interface FixtureDatasetView {
  readonly visibleElements: readonly FixtureElement[];
  readonly outcomeCounts: Readonly<Record<Outcome, number>>;
}

function searchableText(element: FixtureElement): string {
  const classification = element.classification === null
    ? ""
    : `${element.classification.system} ${element.classification.code}`;
  return `${element.id} ${element.label} ${element.type} ${classification}`.toLowerCase();
}

export function createFixtureDatasetIndex(elements: readonly FixtureElement[]): FixtureDatasetIndex {
  const elementsById = new Map<string, FixtureElement>();
  const entries = elements.map((element) => {
    if (elementsById.has(element.id)) throw new RangeError(`Inspection dataset contains a duplicate element id: ${element.id}`);
    elementsById.set(element.id, element);
    return { element, searchText: searchableText(element) };
  });
  return { records: entries.length, entries, elementsById };
}

export function queryFixtureDataset(
  index: FixtureDatasetIndex,
  filter: string,
  typeFilter: string | null,
  outcomeFilter: Outcome | null,
  selectedElementIds: ReadonlySet<string>,
  request: MappingRequest
): FixtureDatasetView {
  const normalizedFilter = filter.trim().toLowerCase();
  const outcomeCounts = Object.fromEntries(OUTCOMES.map((outcome) => [outcome, 0])) as Record<Outcome, number>;
  const visibleElements: FixtureElement[] = [];
  for (const entry of index.entries) {
    const outcome = outcomeForElement(entry.element, selectedElementIds, request);
    outcomeCounts[outcome] += 1;
    if (
      (typeFilter === null || entry.element.type === typeFilter) &&
      (outcomeFilter === null || outcome === outcomeFilter) &&
      entry.searchText.includes(normalizedFilter)
    ) {
      visibleElements.push(entry.element);
    }
  }
  return { visibleElements, outcomeCounts };
}

export function selectedFixtureElements(
  index: FixtureDatasetIndex,
  selectedElementIds: ReadonlySet<string>
): readonly FixtureElement[] {
  const selected: FixtureElement[] = [];
  for (const id of selectedElementIds) {
    const element = index.elementsById.get(id);
    if (element === undefined) throw new RangeError(`Selected element is not in the inspection dataset: ${id}`);
    selected.push(element);
  }
  return selected.sort((left, right) => left.id.localeCompare(right.id));
}
